import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_BASE_URL;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, available: 0 });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');
  const [createMsg, setCreateMsg] = useState({ type: '', text: '' });
  const [adminEmail, setAdminEmail] = useState('');

  const getToken = () => localStorage.getItem('admin_token');

  const fetchCodes = useCallback(async () => {
    const token = getToken();
    if (!token) { navigate('/admin-login', { replace: true }); return; }
    try {
      const res = await axios.get(`${API}/auth/admin/codes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCodes(res.data.codes || []);
      setStats(res.data.stats || {});
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        navigate('/admin-login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    setAdminEmail(localStorage.getItem('admin_email') || '');
    fetchCodes();
  }, [fetchCodes]);

  const handleCreateCode = async (e) => {
    e.preventDefault();
    if (!expiryDate) return;
    setCreating(true);
    setCreateMsg({ type: '', text: '' });
    try {
      const res = await axios.post(
        `${API}/auth/admin/create-code`,
        { code_expiry_date: expiryDate },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setCreateMsg({ type: 'success', text: `Code created: ${res.data.subscription_code}` });
      setExpiryDate('');
      fetchCodes();
    } catch (err) {
      setCreateMsg({ type: 'danger', text: err.response?.data?.error || 'Failed to create code' });
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin-login', { replace: true });
  };

  const getStatusBadge = (code) => {
    const today = new Date().toISOString().slice(0, 10);
    if (code.code_expiry_date < today) return <span className="badge bg-danger">Expired</span>;
    if (code.active_user) return <span className="badge bg-success">Active</span>;
    return <span className="badge bg-warning text-dark">Available</span>;
  };

  const summaryCards = [
    { title: 'Total Codes', value: stats.total, color: 'primary', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
    { title: 'Active', value: stats.active, color: 'success', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { title: 'Expired', value: stats.expired, color: 'danger', icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { title: 'Available', value: stats.available, color: 'info', icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fb' }}>
      {/* Navbar */}
      <nav className="navbar navbar-expand navbar-light bg-white shadow-sm mb-4">
        <div className="container-fluid px-4">
          <span className="navbar-brand fw-bold d-flex align-items-center gap-2">
            <img src={process.env.PUBLIC_URL + '/img/cancepro-logo.png'} alt="CancePro" width="32" height="32" />
            CancePro MedX — Admin
          </span>
          <div className="d-flex align-items-center gap-3">
            <span className="text-muted small">{adminEmail}</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={handleLogout}>Logout</button>
          </div>
        </div>
      </nav>

      <div className="container-fluid px-4">
        <h1 className="h3 mb-3"><strong>Subscription Codes</strong> Dashboard</h1>

        {/* Summary Cards */}
        <div className="row">
          {summaryCards.map((card) => (
            <div key={card.title} className="col-md-3 col-sm-6 mb-3">
              <div className="card">
                <div className="card-body">
                  <div className="row">
                    <div className="col mt-0">
                      <h5 className="card-title">{card.title}</h5>
                    </div>
                    <div className="col-auto">
                      <div className={`stat text-${card.color}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d={card.icon} />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <h1 className="mt-1 mb-3">{loading ? '...' : card.value}</h1>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Code Form */}
        <div className="row mb-4">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Generate New Subscription Code</h5>
              </div>
              <div className="card-body">
                {createMsg.text && (
                  <div className={`alert alert-${createMsg.type} py-2`} role="alert">
                    {createMsg.type === 'success' && <strong className="me-1" style={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>{createMsg.text.split(': ')[1]}</strong>}
                    {createMsg.type === 'success' ? ' — Code generated successfully!' : createMsg.text}
                  </div>
                )}
                <form onSubmit={handleCreateCode} className="d-flex align-items-end gap-3">
                  <div className="flex-grow-1">
                    <label className="form-label">Code Expiry Date <span className="text-danger">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      value={expiryDate}
                      onChange={(e) => setExpiryDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 10)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={creating} style={{ whiteSpace: 'nowrap' }}>
                    {creating ? (
                      <><span className="spinner-border spinner-border-sm me-2" role="status" />Generating...</>
                    ) : (
                      'Generate Code'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Codes Table */}
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">All Subscription Codes</h5>
              </div>
              <div className="card-body">
                <table className="table table-hover my-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Subscription Code</th>
                      <th>Created Date</th>
                      <th>Expiry Date</th>
                      <th>Created By</th>
                      <th>Active User</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {codes.length > 0 ? (
                      codes.map((code, i) => (
                        <tr key={code.id}>
                          <td>{i + 1}</td>
                          <td><code style={{ fontSize: '0.95rem' }}>{code.subscription_code}</code></td>
                          <td>{code.date_time}</td>
                          <td>{code.code_expiry_date}</td>
                          <td>{code.created_by}</td>
                          <td>{code.active_user || <span className="text-muted">—</span>}</td>
                          <td>{getStatusBadge(code)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          {loading ? 'Loading...' : 'No subscription codes yet. Generate one above.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
